import React, { useCallback } from 'react';
import { ProductCard } from '@sg/garnish';

import { useDietaryRestrictions } from '@order/components';
import type { DeliveryOrderDetailInput, RestaurantQuery } from '@order/graphql';
import { useLocalizationContext } from '@order/Localization';
import { telemetryProductFromInput, useTelemetry } from '@order/Telemetry';

import { useProductCallbacks } from '../../../hooks';

// ────────────────────────────────────────────────────────────────────────────────

export const MenuCategoryProduct = (props: MenuCategoryProductProps) => {
  const { product, restaurantSlug, deliveryDetails } = props;

  const {
    name,
    description,
    label,
    asset,
    cost,
    dietaryProperties,
    calories = 0,
  } = product;

  // ─────────────────────────────────────────────────────────────────

  const { t, formatNumber, formatPrice } = useLocalizationContext();
  const getRestrictionNotice = useDietaryRestrictions();
  const { track } = useTelemetry();

  // ─── HELPERS ─────────────────────────────────────────────────────

  const { handleCardPress, handleAddToBag } = useProductCallbacks({
    product,
    restaurantSlug,
    deliveryAddressId: deliveryDetails?.addressId,
    deliveryDetails,
  });

  const onSwipeStart = useCallback(() => {
    track('menu.swipe_left');
  }, [track]);

  const onSwipeToAddToBag = useCallback(async () => {
    const selectedProduct = telemetryProductFromInput({
      ...product,
      location: restaurantSlug,
      quantity: 1,
    });

    await handleAddToBag();
    track('menu.quick_add_to_bag', { selectedProduct });
  }, [handleAddToBag, product, restaurantSlug, track]);

  // ─────────────────────────────────────────────────────────────────

  const restrictionNotice = getRestrictionNotice(dietaryProperties);
  const price = formatPrice(cost, 'USD');
  const caloriesText = `${formatNumber(calories)} ${t(
    'general.calories-short',
  )}`;

  const accessibilityLabel = t('menu.product-card.a11y.label', {
    productName: name,
    description,
    price,
    extra: caloriesText,
  });
  const accessibilityHint = t('menu.product-card.a11y.hint', {
    productName: name,
  });

  // ─────────────────────────────────────────────────────────────────

  return (
    <ProductCard
      restrictionNotice={restrictionNotice}
      name={name}
      description={description}
      label={label?.name}
      price={price}
      calories={caloriesText}
      cloudinaryImageSrc={asset?.url}
      accessibilityRole="link"
      accessibilityLabel={accessibilityLabel}
      accessibilityHint={accessibilityHint}
      onCardPress={handleCardPress}
      onSwipeStart={onSwipeStart}
      onSwipeToAdd={onSwipeToAddToBag}
    />
  );
};

// ─── TYPES ──────────────────────────────────────────────────────────────────────

type MenuCategoryProductProps = {
  readonly product: MenuCategories[number]['products'][number];
  readonly restaurantSlug: string;
  readonly deliveryDetails?: DeliveryOrderDetailInput;
};

type MenuCategories = RestaurantQuery['restaurant']['menu']['categories'];
